import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import PropTypes from "prop-types";

import MKBox from "components/MKBox";
import MKTypography from "components/MKTypography";
import SectionLayout from "reusables/layouts/SectionLayout";

import screenHome from "assets/images/WorkforceApp/screen_home.png";
import screenClockIn from "assets/images/WorkforceApp/screen_clockin.png";
import screenSchedule from "assets/images/WorkforceApp/screen_schedule_upcoming.png";
import screenAlerts from "assets/images/WorkforceApp/screen_alerts.png";

const featureRows = [
  {
    image: screenHome,
    imageAlt: "Home tab showing today's schedule and location",
    title: "Everything at a Glance",
    description:
      "Workers see their clock status, today's shifts, and current location the moment they open the app. No hunting through menus — the home tab surfaces what matters right now.",
    bullets: [
      { icon: "schedule", text: "Live clock-in status" },
      { icon: "today", text: "Today's assigned shifts" },
      { icon: "location_on", text: "Current GPS location" },
    ],
  },
  {
    image: screenClockIn,
    imageAlt: "Clock In/Out screen with GPS map and shift details",
    title: "GPS-Verified Clock In & Out",
    description:
      "Workers clock in directly from the app, and every entry is matched against the job site location. Time entries outside the geofence are flagged automatically for manager review.",
    bullets: [
      { icon: "gps_fixed", text: "Location verified at clock-in" },
      { icon: "flag", text: "Flagged entries for review" },
      { icon: "timer", text: "Automatic grace period handling" },
    ],
    reverse: true,
  },
  {
    image: screenSchedule,
    imageAlt: "Schedule tab showing upcoming shifts and history",
    title: "Smart Scheduling",
    description:
      "Create one-time jobs or set up recurring schedules. Staff see their upcoming shifts in a clean calendar view, with shift details, hours, and rates all in one place.",
    bullets: [
      { icon: "event_repeat", text: "One-time and recurring jobs" },
      { icon: "payments", text: "Rate and hours per shift" },
      { icon: "history", text: "Full schedule history" },
    ],
  },
  {
    image: screenAlerts,
    imageAlt: "Alerts tab showing real-time notifications",
    title: "Real-Time Alerts",
    description:
      "Managers are notified instantly when a worker is late, clocks out early, or a time entry needs review. Staff receive shift reminders and approval confirmations automatically.",
    bullets: [
      { icon: "notifications_active", text: "Late clock-in alerts" },
      { icon: "check_circle", text: "Time entry approval notifications" },
      { icon: "alarm", text: "Upcoming shift reminders" },
    ],
    reverse: true,
  },
];

function PhoneFrame({ src, alt }) {
  return (
    <MKBox display="flex" justifyContent="center">
      <MKBox
        component="img"
        src={src}
        alt={alt}
        sx={{
          width: { xs: "55%", sm: "45%", md: "60%" },
          maxWidth: 280,
          borderRadius: 5,
          boxShadow: "0 24px 64px rgba(0,0,0,0.15)",
        }}
      />
    </MKBox>
  );
}

PhoneFrame.propTypes = {
  src: PropTypes.string.isRequired,
  alt: PropTypes.string.isRequired,
};

function BulletItem({ icon, text }) {
  return (
    <MKBox display="flex" alignItems="center" gap={1.5} mb={1.5}>
      <MKBox
        sx={{
          width: 32,
          height: 32,
          borderRadius: 1,
          background: "#f0f2f5",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <Icon sx={{ fontSize: 18, color: "#32373c" }}>{icon}</Icon>
      </MKBox>
      <MKTypography variant="body2" sx={{ color: "#495057", fontWeight: 500 }}>
        {text}
      </MKTypography>
    </MKBox>
  );
}

BulletItem.propTypes = {
  icon: PropTypes.string.isRequired,
  text: PropTypes.string.isRequired,
};

function Features() {
  return (
    <SectionLayout
      title="Everything you need to manage your team"
      description="Purpose-built for companies who manage field workers across multiple sites."
      contents={
        <MKBox mt={4}>
          {featureRows.map(({ image, imageAlt, title, description, bullets, reverse }, index) => {
            const phoneCol = (
              <Grid item xs={12} md={5} key="phone">
                <PhoneFrame src={image} alt={imageAlt} />
              </Grid>
            );
            const textCol = (
              <Grid item xs={12} md={7} key="text">
                <MKBox pl={{ xs: 0, md: reverse ? 0 : 4 }} pr={{ xs: 0, md: reverse ? 4 : 0 }}>
                  <MKTypography variant="h4" fontWeight="bold" sx={{ color: "#32373c", mb: 2 }}>
                    {title}
                  </MKTypography>
                  <MKTypography variant="body2" sx={{ color: "#6c757d", lineHeight: 1.8, mb: 3 }}>
                    {description}
                  </MKTypography>
                  {bullets.map((b) => (
                    <BulletItem key={b.text} icon={b.icon} text={b.text} />
                  ))}
                </MKBox>
              </Grid>
            );

            return (
              <Container key={title}>
                <Grid
                  container
                  spacing={6}
                  alignItems="center"
                  sx={{ mb: index < featureRows.length - 1 ? { xs: 8, md: 12 } : 0 }}
                >
                  {reverse ? [textCol, phoneCol] : [phoneCol, textCol]}
                </Grid>
              </Container>
            );
          })}
        </MKBox>
      }
    />
  );
}

export default Features;
